import React, { useState, useEffect } from 'react';
import NPackItem from "./NPackItem";
import { useQuery } from "@tanstack/react-query";
import useUser from "../../lib/useUser";
import { Box, Grid, VStack, Center, Container, useMediaQuery, 
  CircularProgress, Heading , useToast} from "@chakra-ui/react";
import { getMyNPacks } from "../../apisntypes/packapis"; 
import { INPack } from "../../apisntypes/packtypes"; 
import { PackMenu } from './PackMenu';
import ProtectedPage from '../ProtectedPage';
import { useNavigate } from 'react-router-dom';

interface NPacksApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: INPack[]; // Define INPack interface according to the structure of your data
}

export default function MyPacks() {
  const { userLoading, user, isLoggedIn } = useUser();
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // Track total number of items
  const [data, setData] = useState<NPacksApiResponse>({ count: 0, next: null, previous: null, results: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [currentItemsCount, setCurrentItemsCount] = useState(0);
  const [renderCount, setRenderCount] = useState(1);
  const [isLargeScreen] = useMediaQuery("(min-width: 768px)");
  const toast = useToast();
  const [favoritePacks, setFavoritePacks] = useState<number[]>([]);

  const fetchMyNPacks = async (pageNum: number) => {
    setIsLoading(true);
    try {
      const response = await getMyNPacks(pageNum);
      if (response) {
        const newData = pageNum === 1 ? response.results : [...data.results, ...response.results];

        // Extract the list of favorite packs from the response
        const favoritePackIds = response.results
        .filter(pack => pack.isFavorite === 'y')
        .map(pack => pack.id);

        setFavoritePacks(favoritePackIds);

        setData((prevData) => ({
          ...prevData,
          results: newData,
        }));

        setPage((prevPage) => prevPage + 1);
        setTotalItems(response.count); 
        setCurrentItemsCount(prevCount => prevCount + response.results.length);
      } else {
        console.error("Empty response received from server");
        toast({
          status: "error",
          title: "Empty response received from server",
          position: "bottom-right",
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        status: "error",
        title: "Error fetching data",
        position: "bottom-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
    fetchMyNPacks(page);
    }
  }, [isLoggedIn]); // Fetch initial data when component mount

   
  const handleScroll = () => {
    const scrollPosition = window.innerHeight + window.scrollY;
    const pageHeight = document.body.scrollHeight;
    if (scrollPosition >= pageHeight - 20 && !isLoading && currentItemsCount < totalItems) {
      fetchMyNPacks(page);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isLoading, data.results.length, totalItems]);

  const handleDeleteFavPack = (packId: number) => {
    // Your logic to handle deletion
  };

  return (
    <ProtectedPage>

      <Container maxW="container.lg">
        <VStack m={6}>
          <Box w="100%" textAlign="center">
            <Heading pr="25px">My Note Packs</Heading>
          </Box>
          <PackMenu nPackPage="priv" />
        </VStack>
        
        <Container maxW="container.lg">
          {isLoading && <CircularProgress isIndeterminate color="green.300" />}
          {data && Array.isArray(data.results) ? (
          <Grid
          templateColumns={{
            base: "repeat(2, 1fr)", // 2 columns for small screens
            sm: "repeat(2, 1fr)",  // 3 columns for small to medium screens
            md: "repeat(3, 1fr)",  // 4 columns for medium to large screens
            lg: "repeat(4, 1fr)",  // 5 columns for large screens and above
          }}
          gap={4}
          alignItems="center"
          my="2"
        >
            {data.results.map((mcate, index) => (
              <NPackItem
                key={index}
                pk={mcate.id}
                pack_pk={mcate.id}
                title={mcate.title}
                addr={mcate.addr}
                desc={mcate.desc}
                inputString=""
                creator={mcate.creator}
                postCount={mcate.postCount}
                cateCount={mcate.cateCount}
                pubOpt={mcate.pubOpt}
                isGroupOpen={mcate.isGroupOpen}
                prompt={mcate.prompt}
                cateOpt={mcate.cateOpt}
                noteOpt={mcate.noteOpt}
                commentOpt={mcate.commentOpt}
                updated_at={mcate.updated_at}
                renderCount={renderCount+index}
                nPackPage="priv"
                isFavorite={mcate.isFavorite}
                favoritePacks={favoritePacks}
                language={mcate.language}
                setFavoritePacks={setFavoritePacks}
                is_newpost={mcate.is_newpost}
              />
            ))}
            {isLoading && <CircularProgress isIndeterminate color="green.300" />}
          </Grid>
        ) : 
        null          
        }
          {data.results.length === 0 ?
            <Center>You didn't make any Note Pack.</Center>
            : null   }
        </Container>
      </Container>
    </ProtectedPage>
  );

}